import { useCallback, useEffect, useRef, useState } from 'react'
import { URL as BACKEND_URL } from '../../gql/client'
import { useGlobalWebSocket } from '../../providers/WebSocketProvider'
import { useLatency } from '../../hooks/useLatency'

interface VicidialHealth {
  ok: boolean
  rtt_ms: number | null
  status_code?: number
  error?: string
}

interface SystemHealth {
  broker_ok: boolean
  queue_depth: number | null
  active_workers: number
  active_tasks: number
  worker_names: string[]
}

type Status = 'good' | 'warn' | 'error' | 'loading' | 'idle'

const WS_THRESHOLDS = { good: 100, warn: 300 }
const HTTP_THRESHOLDS = { good: 150, warn: 400 }
const VICIDIAL_THRESHOLDS = { good: 200, warn: 500 }
const QUEUE_THRESHOLDS = { good: 10, warn: 50 }

const STATUS_COLOR: Record<Status, string> = {
  good: '#50cd89',
  warn: '#ffc700',
  error: '#f1416c',
  loading: '#009ef7',
  idle: '#b5b5c3',
}

const STATUS_LABEL: Record<Status, string> = {
  good: 'OK',
  warn: 'LENTO',
  error: 'ERROR',
  loading: '…',
  idle: '—',
}

function getRttStatus(rtt: number | null, thresholds: { good: number; warn: number }): Status {
  if (rtt === null) return 'error'
  if (rtt < thresholds.good) return 'good'
  if (rtt < thresholds.warn) return 'warn'
  return 'error'
}

function getQueueStatus(depth: number | null): Status {
  if (depth === null) return 'error'
  if (depth < QUEUE_THRESHOLDS.good) return 'good'
  if (depth < QUEUE_THRESHOLDS.warn) return 'warn'
  return 'error'
}

// VU-meter: 8 vertical segments that fill left-to-right based on RTT
function LatencyMeter({ rtt, thresholds }: { rtt: number | null; thresholds: { good: number; warn: number } }) {
  const SEGMENTS = 8
  const max = thresholds.warn * 1.5
  const filled = rtt === null ? 0 : Math.min(SEGMENTS, Math.ceil((rtt / max) * SEGMENTS))

  return (
    <div style={{ display: 'flex', gap: 3, alignItems: 'flex-end', height: 22, margin: '10px 0 6px' }}>
      {Array.from({ length: SEGMENTS }, (_, i) => {
        const active = i < filled
        const segColor = i < 4 ? '#50cd89' : i < 6 ? '#ffc700' : '#f1416c'
        return (
          <div
            key={i}
            style={{
              width: 9,
              height: 8 + i * 1.8,
              borderRadius: 2,
              backgroundColor: active ? segColor : '#e9ecef',
              transition: 'background-color 0.25s ease',
            }}
          />
        )
      })}
    </div>
  )
}

function MetricCard({
  label,
  sublabel,
  rtt,
  thresholds,
  status,
}: {
  label: string
  sublabel: string
  rtt: number | null
  thresholds: { good: number; warn: number }
  status: Status
}) {
  const color = STATUS_COLOR[status]
  return (
    <div
      className='card h-100'
      style={{ borderTop: `3px solid ${color}`, transition: 'border-color 0.3s ease' }}
    >
      <div className='card-body p-5'>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.12em',
            color: '#b5b5c3',
            textTransform: 'uppercase',
            marginBottom: 2,
          }}
        >
          {sublabel}
        </div>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#3f4254', marginBottom: 14 }}>
          {label}
        </div>
        <div
          style={{
            fontFamily: "'SF Mono','Fira Code','Cascadia Code','Consolas',monospace",
            fontSize: 40,
            fontWeight: 700,
            color: '#1c2333',
            lineHeight: 1,
          }}
        >
          {rtt === null ? '—' : rtt}
          {rtt !== null && (
            <span style={{ fontSize: 13, fontWeight: 400, color: '#b5b5c3', marginLeft: 4 }}>ms</span>
          )}
        </div>
        <LatencyMeter rtt={rtt} thresholds={thresholds} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
          <div
            style={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              backgroundColor: color,
              transition: 'background-color 0.3s ease',
            }}
          />
          <span
            style={{
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: '0.1em',
              color,
              transition: 'color 0.3s ease',
            }}
          >
            {STATUS_LABEL[status]}
          </span>
        </div>
      </div>
    </div>
  )
}

const AUTO_REFRESH_SEC = 30

export const SystemMonitorPage = () => {
  const socket = useGlobalWebSocket()
  const { result: latency, measure } = useLatency(socket)

  const [vicidial, setVicidial] = useState<VicidialHealth | null>(null)
  const [system, setSystem] = useState<SystemHealth | null>(null)
  const [serverLoading, setServerLoading] = useState(false)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [countdown, setCountdown] = useState(AUTO_REFRESH_SEC)
  const countdownRef = useRef<number | null>(null)

  const fetchServerHealth = useCallback(async () => {
    setServerLoading(true)
    try {
      const [vRes, sRes] = await Promise.allSettled([
        fetch(`${BACKEND_URL}/health/vicidial`).then((r) => r.json()),
        fetch(`${BACKEND_URL}/health/system`).then((r) => r.json()),
      ])
      if (vRes.status === 'fulfilled') setVicidial(vRes.value as VicidialHealth)
      if (sRes.status === 'fulfilled') setSystem(sRes.value as SystemHealth)
      setLastUpdated(new Date())
    } finally {
      setServerLoading(false)
    }
  }, [])

  const runAll = useCallback(async () => {
    setCountdown(AUTO_REFRESH_SEC)
    await Promise.all([measure(), fetchServerHealth()])
  }, [measure, fetchServerHealth])

  useEffect(() => {
    runAll()
    const interval = window.setInterval(runAll, AUTO_REFRESH_SEC * 1000)
    return () => window.clearInterval(interval)
  }, [runAll])

  useEffect(() => {
    countdownRef.current = window.setInterval(() => {
      setCountdown((c) => (c <= 1 ? AUTO_REFRESH_SEC : c - 1))
    }, 1000)
    return () => {
      if (countdownRef.current) window.clearInterval(countdownRef.current)
    }
  }, [])

  const isLoading = serverLoading || latency.measuring

  const wsStatus: Status = latency.measuring
    ? 'loading'
    : latency.wsRtt !== null
    ? getRttStatus(latency.wsRtt, WS_THRESHOLDS)
    : latency.error
    ? 'error'
    : 'idle'

  const httpStatus: Status = latency.measuring
    ? 'loading'
    : latency.httpRtt !== null
    ? getRttStatus(latency.httpRtt, HTTP_THRESHOLDS)
    : latency.error
    ? 'error'
    : 'idle'

  const vicidialStatus: Status = serverLoading
    ? 'loading'
    : vicidial === null
    ? 'idle'
    : !vicidial.ok
    ? 'error'
    : getRttStatus(vicidial.rtt_ms, VICIDIAL_THRESHOLDS)

  const queueStatus: Status = system === null ? 'idle' : !system.broker_ok ? 'error' : getQueueStatus(system.queue_depth)
  const brokerColor = system === null ? '#b5b5c3' : system.broker_ok ? '#50cd89' : '#f1416c'

  return (
    <div className='container-fluid p-8'>
      {/* Header */}
      <div className='d-flex align-items-start justify-content-between flex-wrap gap-4 mb-8'>
        <div>
          <h1 className='page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0'>
            System Monitor
          </h1>
          <span className='text-muted fs-7 fw-semibold mt-1 d-block'>
            Diagnóstico de conectividad · Vicidial · Celery / Valkey
          </span>
        </div>
        <div className='d-flex align-items-center gap-4 flex-wrap'>
          {lastUpdated && (
            <span
              className='text-muted fs-8'
              style={{ fontFamily: "'SF Mono','Fira Code',monospace" }}
            >
              {lastUpdated.toLocaleTimeString('es', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              })}
            </span>
          )}
          <span
            className='badge rounded-pill'
            style={{
              backgroundColor: '#f1faff',
              color: '#009ef7',
              fontSize: 11,
              fontFamily: "'SF Mono','Fira Code',monospace",
              fontWeight: 600,
              padding: '4px 10px',
            }}
          >
            auto en {countdown}s
          </span>
          <button
            className='btn btn-sm btn-primary'
            onClick={runAll}
            disabled={isLoading}
          >
            {isLoading && <span className='spinner-border spinner-border-sm me-2' />}
            Actualizar
          </button>
        </div>
      </div>

      {/* Section label: Capa 1 + 2 */}
      <div
        className='text-muted fw-bold mb-4'
        style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase' }}
      >
        Latencia de red
      </div>

      {/* RTT Cards */}
      <div className='row g-5 mb-8'>
        <div className='col-12 col-md-4'>
          <MetricCard
            label='WebSocket'
            sublabel='Canal tiempo real'
            rtt={latency.wsRtt}
            thresholds={WS_THRESHOLDS}
            status={wsStatus}
          />
        </div>
        <div className='col-12 col-md-4'>
          <MetricCard
            label='HTTP / REST'
            sublabel='Solicitudes API'
            rtt={latency.httpRtt}
            thresholds={HTTP_THRESHOLDS}
            status={httpStatus}
          />
        </div>
        <div className='col-12 col-md-4'>
          <MetricCard
            label='Vicidial API'
            sublabel='Servidor → Vicidial (VPC)'
            rtt={vicidial?.rtt_ms ?? null}
            thresholds={VICIDIAL_THRESHOLDS}
            status={vicidialStatus}
          />
        </div>
      </div>

      {/* Section label: Capa 3 */}
      <div
        className='text-muted fw-bold mb-4'
        style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase' }}
      >
        Cola de tareas
      </div>

      {/* Celery / Valkey */}
      <div className='card mb-5'>
        <div className='card-body p-6'>
          <div className='row g-6 align-items-start'>
            {/* Queue depth */}
            <div className='col-6 col-md-3'>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  color: '#b5b5c3',
                  textTransform: 'uppercase',
                  marginBottom: 8,
                }}
              >
                Tareas en cola
              </div>
              <div
                style={{
                  fontFamily: "'SF Mono','Fira Code',monospace",
                  fontSize: 44,
                  fontWeight: 700,
                  color: STATUS_COLOR[queueStatus],
                  lineHeight: 1,
                  transition: 'color 0.3s ease',
                }}
              >
                {system?.queue_depth ?? '—'}
              </div>
              <div style={{ fontSize: 11, color: '#b5b5c3', marginTop: 6 }}>pendientes · Valkey</div>
            </div>

            {/* Active workers */}
            <div className='col-6 col-md-3'>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  color: '#b5b5c3',
                  textTransform: 'uppercase',
                  marginBottom: 8,
                }}
              >
                Workers activos
              </div>
              <div
                style={{
                  fontFamily: "'SF Mono','Fira Code',monospace",
                  fontSize: 44,
                  fontWeight: 700,
                  color:
                    system === null ? '#b5b5c3' : system.active_workers > 0 ? '#50cd89' : '#f1416c',
                  lineHeight: 1,
                  transition: 'color 0.3s ease',
                }}
              >
                {system?.active_workers ?? '—'}
              </div>
              <div style={{ fontSize: 11, color: '#b5b5c3', marginTop: 6 }}>
                {system?.active_tasks ?? 0} ejecutando ahora
              </div>
            </div>

            {/* Broker */}
            <div className='col-6 col-md-3'>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  color: '#b5b5c3',
                  textTransform: 'uppercase',
                  marginBottom: 8,
                }}
              >
                Broker
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                <div
                  style={{
                    width: 11,
                    height: 11,
                    borderRadius: '50%',
                    backgroundColor: brokerColor,
                    boxShadow: system?.broker_ok ? `0 0 0 3px rgba(80,205,137,0.2)` : undefined,
                    flexShrink: 0,
                    transition: 'background-color 0.3s ease',
                  }}
                />
                <span
                  style={{
                    fontFamily: "'SF Mono','Fira Code',monospace",
                    fontSize: 13,
                    fontWeight: 700,
                    color: brokerColor,
                    letterSpacing: '0.05em',
                  }}
                >
                  {system === null ? '—' : system.broker_ok ? 'CONECTADO' : 'SIN CONEXIÓN'}
                </span>
              </div>
            </div>

            {/* Worker names */}
            <div className='col-6 col-md-3'>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  color: '#b5b5c3',
                  textTransform: 'uppercase',
                  marginBottom: 8,
                }}
              >
                Workers
              </div>
              {system && system.worker_names.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {system.worker_names.map((w) => (
                    <div key={w} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor: '#50cd89',
                          flexShrink: 0,
                        }}
                      />
                      <span
                        style={{
                          fontFamily: "'SF Mono','Fira Code',monospace",
                          fontSize: 11,
                          color: '#3f4254',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {w}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <span style={{ fontFamily: 'monospace', fontSize: 13, color: '#f1416c' }}>
                  Sin workers
                </span>
              )}
            </div>
          </div>

          {/* Queue warning banner */}
          {system && system.queue_depth !== null && system.queue_depth >= QUEUE_THRESHOLDS.good && (
            <div
              className='mt-6 rounded p-4'
              style={{
                backgroundColor: system.queue_depth >= QUEUE_THRESHOLDS.warn ? '#fff5f8' : '#fffbf0',
                borderLeft: `3px solid ${STATUS_COLOR[queueStatus]}`,
              }}
            >
              <span style={{ fontSize: 12, color: '#3f4254' }}>
                <strong>Cola acumulada — {system.queue_depth} tareas pendientes.</strong>{' '}
                {system.queue_depth >= QUEUE_THRESHOLDS.warn
                  ? 'Workers posiblemente saturados: las asignaciones pueden llegar tarde a Vicidial o nunca ejecutarse.'
                  : 'Monitorea si el número sigue creciendo.'}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Error alerts */}
      {latency.error && (
        <div className='alert alert-danger d-flex align-items-center' role='alert'>
          <span className='fs-7'>
            <strong>Error de latencia:</strong> {latency.error}
          </span>
        </div>
      )}
      {vicidial && !vicidial.ok && (
        <div className='alert alert-danger' role='alert'>
          <strong>Vicidial no responde:</strong>{' '}
          {vicidial.error ?? 'Sin detalles — revisa la conexión VPC o el estado del servidor.'}
        </div>
      )}
    </div>
  )
}
